import { useRef, useCallback } from 'react'

const MILLISECONDS_PER_SECOND = 1e3
const DEFAULT_SIGNIFICANT_CHANGE_THRESHOLD = 10

interface UseMapLibreFrameRateOptions {
  significantChangeThreshold?: number
  onSignificantChange?: (fps: number, change: number) => void
}

/**
 * Hook for monitoring MapLibre frame rate.
 * Enable when the map is moving, disable when it stops.
 */
export function useMapLibreFrameRate(options: UseMapLibreFrameRateOptions = {}) {
  const { significantChangeThreshold = DEFAULT_SIGNIFICANT_CHANGE_THRESHOLD, onSignificantChange } =
    options

  const enabledRef = useRef(false)
  const framesRef = useRef(0)
  const timeRef = useRef(performance.now())
  const previousFpsRef = useRef<number | null>(null)
  const optionsRef = useRef({ significantChangeThreshold, onSignificantChange })
  optionsRef.current = { significantChangeThreshold, onSignificantChange }

  const enable = useCallback(() => {
    enabledRef.current = true
    framesRef.current = 0
    timeRef.current = performance.now()
    previousFpsRef.current = null
  }, [])

  const disable = useCallback(() => {
    enabledRef.current = false
  }, [])

  const onRender = useCallback(() => {
    if (!enabledRef.current) return

    framesRef.current++
    const now = performance.now()

    if (now >= timeRef.current + MILLISECONDS_PER_SECOND) {
      const fps =
        Math.round((MILLISECONDS_PER_SECOND * framesRef.current) / (now - timeRef.current)) || 0

      const { significantChangeThreshold, onSignificantChange } = optionsRef.current
      if (onSignificantChange && previousFpsRef.current !== null) {
        const change = fps - previousFpsRef.current
        if (Math.abs(change) >= significantChangeThreshold) {
          onSignificantChange(fps, change)
        }
      }

      previousFpsRef.current = fps
      framesRef.current = 0
      timeRef.current = now
    }
  }, [])

  return { onRender, enable, disable }
}

export default useMapLibreFrameRate
